import type { ActionGuide, TargetRecord } from "./types";
import type {
  SignalGuide,
  SignalGuideLink,
} from "../../Store/Reducers/SignalFeedSlice";

/** Shared by the Action Guides editor and the Review and apply modal, so
 * every admin preview renders a guide exactly as a card would. */

/** Preview values chosen to make each signal's condition true, so the
 * "why" text reads the way users will see it. */
const BASE_SAMPLE: Record<string, unknown> = {
  al: 2,
  rl: 3,
  ah: 60,
  bh: 55,
  renewal_days: 90,
  al_stale_days: 42,
  target_al: 4,
  bh_financial: 44,
  bh_leadership: 40,
  bh_legal: 50,
  bh_growth: 82,
  cluster: "defend",
  contact_stage: "added",
  portfolio_risk: "high",
  expansion_opportunity: "strong",
  rl_conditions: "blocked",
};
const SAMPLE_BY_SIGNAL: Record<string, Record<string, unknown>> = {
  "warm-uncommitted": { al: 1, rl: 4 },
  "ready-to-advance": { al: 3, rl: 4 },
  "fragile-alignment": { al: 4, rl: 1 },
  "dependent-alignment": { al: 4, rl: 3 },
  "rl-low-for-cluster": { rl: 2, ah: 78 },
  "renewal-no-foundation": { renewal_days: 25, al: 1, rl: 2 },
  "renewal-warning": { renewal_days: 95 },
  "bh-ah-aircover": { bh: 78, ah: 58 },
  "kpi-threshold": { ah: 44 },
  "triage-bh-concern": { bh: 38, ah: 47, cluster: "triage" },
  "triage-stale-renewal": { cluster: "triage", renewal_days: 45 },
  "cluster-triage": { cluster: "triage" },
  "al-unassessed": { al: null, renewal_days: 45 },
  "contacts-missing": { contact_stage: "none", renewal_days: 45 },
  "rl-unassessed": { contact_stage: "mapped", rl: 0 },
  "strategic-alignment": { al: 4, rl: 5 },
  balanced: { al: 2, rl: 3 },
};

/** Example triggered fields for a signal's preview. */
export const sampleFields = (
  definitionId: string
): Record<string, unknown> => ({
  ...BASE_SAMPLE,
  ...SAMPLE_BY_SIGNAL[definitionId],
});

/** Mirrors the backend's _step_link: an unavailable target uses its
 * fallback, or the step shows no link. */
export const stepLink = (
  targetId: string,
  targets: Map<string, TargetRecord>
): SignalGuideLink | null => {
  if (!targetId) return null;
  let record = targets.get(targetId);
  if (record && !record.available && record.fallback_target)
    record = targets.get(record.fallback_target);
  if (!record || !record.available || !record.kind) return null;
  return {
    target: record.id,
    label: record.label,
    kind: record.kind,
    path: record.kind === "path" ? record.route : null,
  };
};

export const toUserGuide = (
  g: ActionGuide,
  targets: Map<string, TargetRecord>
): SignalGuide => ({
  title: g.title,
  why: g.why,
  clears_when: g.clears_when,
  steps: g.steps.map(s => ({
    title: s.title,
    body: s.body,
    link: stepLink(s.target, targets),
  })),
});
